import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as TecladoReact,
  type ReactNode,
} from 'react';
import {
  BLOQUE0_PAGINA2,
  BLOQUE0_PAGINA3,
  BLOQUES,
  CONSENT_TEXT,
  DESCRIPTORES_NIVEL,
  INTRO_V02,
  LEGAL_PENDING,
  TRANSICION_DIMENSION,
  fechaRecomendacion,
  fichaPorCodigo,
  urlCapacidad,
  urlRecomendacion,
  type PreguntaPuntuable,
} from '@cessi/content';
import { esSocioCessiValido } from '@cessi/content/socios';
import {
  BENCHMARK_MIN_N,
  DIMENSION_LABELS,
  letraParaUi,
  normalizarTelefonoE164,
  organizacionesFaltantesBenchmark,
  websiteConHttps,
  websiteSinProtocolo,
  type OpcionLetra,
} from '@cessi/shared';
import { etiquetaPregunta } from '../lib/dimLabels';
import { Boton, Panel, Progreso } from '../ui';
import { SocioCombobox } from '../components/SocioCombobox';
import { Landing } from '../components/Landing';
import { BrandLockup } from '../components/BrandLockup';
import { FooterFramework } from '../components/FooterFramework';
import { OtpBoxes } from '../components/OtpBoxes';
import { IndiceDial, RadarChart } from '../components/RadarChart';
import {
  fetchResults,
  patchDraft,
  requestOtp,
  submitSubmission,
  verifyOtp,
  type BenchmarkPayload,
  type ScorePayload,
} from '../lib/api';
import { descargarPerfilPdf } from '../lib/perfilPdf';
import { FRAMEWORK_BASE, FRAMEWORK_SELLO, enlacesResultados } from '../lib/framework';
import {
  clearDraft,
  draftToServerPayload,
  emptyDraft,
  loadDraft,
  rememberOrg,
  saveDraft,
  type DraftState,
} from '../lib/draft';
import {
  flatQuestionIndex,
  progressFraction,
  totalPreguntasPuntuables,
  WIZARD_STEPS,
  type WizardStep,
} from '../lib/progress';
import {
  MAX_SECTORES,
  parseSectores,
  SECTOR_MULTIPLE,
  serializarSectores,
  toggleSector,
} from '../lib/sectores';

const AUTO_ADVANCE_MS = 400;
const DRAFT_DEBOUNCE_MS = 800;
/** Afordancia de prueba: reiniciar el formulario desde resultados. Apagar antes del conversatorio. */
const MOSTRAR_REINICIO_TEST = true;
const REENVIO_ESPERA_S = 30;
const TOTAL_PREGUNTAS = totalPreguntasPuntuables();
const LETRAS: OpcionLetra[] = ['a', 'b', 'c', 'd'];

function honeypotValue(): string {
  return (document.getElementById('company_fax') as HTMLInputElement | null)?.value ?? '';
}

function buildSubmitBody(draft: DraftState) {
  return {
    schemaVersion: 4 as const,
    honeypot: honeypotValue(),
    nombre: draft.nombre,
    email: draft.email,
    organizacion: draft.organizacion,
    website: draft.website,
    linkedin: draft.linkedin || undefined,
    consentimiento: true as const,
    p0_1: draft.p0_1,
    p0_2: draft.p0_2,
    p0_3: draft.p0_3,
    p0_4: draft.p0_4,
    p0_5: draft.p0_5,
    p0_6: draft.p0_6,
    p0_7: draft.p0_7,
    p0_8: draft.p0_8,
    p0_9: draft.p0_9 || undefined,
    p0_10: draft.p0_10 || undefined,
    EV1: draft.respuestas.EV1,
    EV2: draft.respuestas.EV2,
    EV3: draft.respuestas.EV3,
    EV4: draft.respuestas.EV4,
    EV5: draft.respuestas.EV5,
    PC1: draft.respuestas.PC1,
    PC2: draft.respuestas.PC2,
    PC3: draft.respuestas.PC3,
    PC4: draft.respuestas.PC4,
    OD1: draft.respuestas.OD1,
    OD2: draft.respuestas.OD2,
    OD3: draft.respuestas.OD3,
    OD4: draft.respuestas.OD4,
    DC1: draft.respuestas.DC1,
    DC2: draft.respuestas.DC2,
    DC3: draft.respuestas.DC3,
    DC4: draft.respuestas.DC4,
    GS1: draft.respuestas.GS1,
    GS2: draft.respuestas.GS2,
    GS3: draft.respuestas.GS3,
    GS4: draft.respuestas.GS4,
    IN1: draft.respuestas.IN1,
    IN2: draft.respuestas.IN2,
    IN3: draft.respuestas.IN3,
    IN4: draft.respuestas.IN4,
  };
}

export function Wizard() {
  const [draft, setDraft] = useState<DraftState>(() => loadDraft());
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpInvalido, setOtpInvalido] = useState(false);
  const [reenvioEn, setReenvioEn] = useState(0);
  const [otpPhaseDone, setOtpPhaseDone] = useState(
    () => loadDraft().otpVerified || loadDraft().otpBypass,
  );
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const vistaRef = useRef<HTMLElement>(null);

  const step = WIZARD_STEPS[draft.stepIndex] ?? WIZARD_STEPS[0]!;
  const progress = progressFraction(draft.stepIndex);
  const enOtp = step.kind === 'registro_b' && !otpPhaseDone;

  useEffect(() => {
    saveDraft(draft);
  }, [draft]);

  // Autosave remoto con debounce si hay draft_token
  useEffect(() => {
    if (!draft.draft_token) return;
    if (draftTimer.current) clearTimeout(draftTimer.current);
    draftTimer.current = setTimeout(() => {
      void patchDraft(draft.draft_token!, draftToServerPayload(draft));
    }, DRAFT_DEBOUNCE_MS);
    return () => {
      if (draftTimer.current) clearTimeout(draftTimer.current);
    };
  }, [draft]);

  useEffect(() => {
    return () => {
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
    };
  }, []);

  // Cada pantalla arranca arriba y con el foco en el contenido (lectores de pantalla).
  useEffect(() => {
    if (step.kind === 'landing' || enOtp) return;
    window.scrollTo({ top: 0 });
    vistaRef.current?.focus({ preventScroll: true });
  }, [draft.stepIndex, step.kind, enOtp]);

  // Cuenta regresiva para reenviar el código.
  useEffect(() => {
    if (reenvioEn <= 0) return;
    const id = setTimeout(() => setReenvioEn((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [reenvioEn]);

  const setField = <K extends keyof DraftState>(key: K, value: DraftState[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
  };

  const goTo = (index: number) => {
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
    setError(null);
    setField('stepIndex', Math.max(0, Math.min(WIZARD_STEPS.length - 1, index)));
  };

  /**
   * Descarta el envío actual y arranca una autoevaluación nueva. El submit ya
   * quedó persistido en el servidor; acá solo se limpia el estado local.
   */
  const reiniciarEvaluacion = () => {
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
    if (draftTimer.current) clearTimeout(draftTimer.current);
    clearDraft();
    setError(null);
    setAviso(null);
    setOtpCode('');
    setOtpSent(false);
    setOtpInvalido(false);
    setReenvioEn(0);
    setOtpPhaseDone(false);
    setDraft(emptyDraft());
    window.scrollTo({ top: 0 });
  };

  const next = () => goTo(draft.stepIndex + 1);
  const back = () => goTo(draft.stepIndex - 1);

  const setRespuesta = (id: string, letra: OpcionLetra, autoAdvance: boolean) => {
    setError(null);
    setDraft((d) => ({
      ...d,
      respuestas: { ...d.respuestas, [id]: letra },
    }));
    if (autoAdvance) {
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
      advanceTimer.current = setTimeout(() => {
        setDraft((d) => ({
          ...d,
          stepIndex: Math.min(WIZARD_STEPS.length - 1, d.stepIndex + 1),
        }));
      }, AUTO_ADVANCE_MS);
    }
  };

  const validate = (s: WizardStep = step): string | null => {
    if (s.kind === 'registro_a') {
      if (!draft.nombre.trim() || draft.nombre.trim().length < 2) return 'Ingrese nombre y apellido.';
      if (!draft.email.trim() || !draft.email.includes('@')) return 'Email corporativo inválido.';
      if (!draft.consentimiento) return 'Debe aceptar el consentimiento.';
      if (draft.otpChannel === 'sms') {
        if (!draft.phone.trim() || !normalizarTelefonoE164(draft.phone)) {
          return 'Ingrese un celular válido (ej. +54 9 11 1234-5678).';
        }
      }
    }
    if (s.kind === 'registro_b') {
      if (!otpPhaseDone) return 'Verifique el código o continúe sin OTP si el backend no está listo.';
      if (!draft.organizacion.trim()) return 'Seleccione la organización.';
      if (!esSocioCessiValido(draft.organizacion)) {
        return 'Elija una empresa del listado de socios CESSI.';
      }
      const web = websiteConHttps(draft.website);
      if (!web) return 'Ingrese el sitio web (ej. www.empresa.com).';
      try {
        const u = new URL(web);
        if (!u.hostname.includes('.')) {
          return 'Ingrese un sitio web válido (ej. www.empresa.com).';
        }
      } catch {
        return 'Ingrese un sitio web válido (ej. www.empresa.com).';
      }
    }
    if (s.kind === 'bloque0') {
      const list = s.page === 2 ? BLOQUE0_PAGINA2 : BLOQUE0_PAGINA3;
      for (const p of list) {
        if (!draft[p.id as keyof DraftState]) return `Complete: ${p.texto}`;
      }
    }
    if (s.kind === 'pregunta') {
      const preg = BLOQUES[s.bloqueIdx]?.preguntas[s.pregIdx];
      if (preg && !draft.respuestas[preg.id]) return 'Seleccione una opción.';
    }
    if (s.kind === 'review') {
      for (const b of BLOQUES) {
        for (const preg of b.preguntas) {
          if (!draft.respuestas[preg.id]) return `Falta responder: ${etiquetaPregunta(preg.id)}`;
        }
      }
    }
    return null;
  };

  const onContinue = () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    next();
  };

  const sendOtp = async () => {
    const err = validate({ kind: 'registro_a' });
    if (err) {
      setError(err);
      return;
    }
    setSending(true);
    setError(null);
    setAviso(null);
    try {
      const res = await requestOtp({
        email: draft.email.trim(),
        nombre: draft.nombre.trim(),
        consentimiento: true,
        channel: draft.otpChannel,
        phone: draft.otpChannel === 'sms' ? draft.phone.trim() : undefined,
      });
      if (!res.ok) {
        if (res.error.unavailable) {
          setAviso('OTP pendiente de backend. Puede continuar el registro sin verificación por ahora.');
          setField('otpBypass', true);
          setOtpPhaseDone(true);
          next();
          return;
        }
        setError(res.error.message);
        return;
      }
      if (res.data.codeDevOnly) {
        setAviso(`Demo MVP: su código es ${res.data.codeDevOnly}`);
        setOtpCode(res.data.codeDevOnly);
      }
      setOtpSent(true);
      setReenvioEn(REENVIO_ESPERA_S);
      next();
    } finally {
      setSending(false);
    }
  };

  const resendOtp = async () => {
    if (reenvioEn > 0 || sending) return;
    setSending(true);
    setError(null);
    setOtpInvalido(false);
    try {
      const res = await requestOtp({
        email: draft.email.trim(),
        nombre: draft.nombre.trim(),
        consentimiento: true,
        channel: draft.otpChannel,
        phone: draft.otpChannel === 'sms' ? draft.phone.trim() : undefined,
      });
      if (!res.ok) {
        if (res.error.unavailable) {
          setAviso('OTP pendiente de backend. Puede continuar sin verificación.');
          setField('otpBypass', true);
          setOtpPhaseDone(true);
          return;
        }
        setError(res.error.message);
        return;
      }
      if (res.data.codeDevOnly) {
        setAviso(`Demo MVP: su código es ${res.data.codeDevOnly}`);
        setOtpCode(res.data.codeDevOnly);
      } else {
        const dest =
          draft.otpChannel === 'sms'
            ? draft.phone.trim()
            : draft.email.trim();
        setAviso(`Reenviamos el código a ${dest}.`);
      }
      setOtpSent(true);
      setReenvioEn(REENVIO_ESPERA_S);
    } finally {
      setSending(false);
    }
  };

  const confirmOtp = async () => {
    if (otpCode.replace(/\D/g, '').length !== 6) {
      setOtpInvalido(true);
      setError('Ingrese el código de 6 dígitos.');
      return;
    }
    setSending(true);
    setError(null);
    try {
      const res = await verifyOtp({ email: draft.email.trim(), code: otpCode.replace(/\D/g, '') });
      if (!res.ok) {
        if (res.error.unavailable) {
          setAviso('OTP pendiente de backend. Continuamos sin verificación.');
          setField('otpBypass', true);
          setOtpPhaseDone(true);
          return;
        }
        setOtpInvalido(true);
        setError(res.error.message);
        return;
      }
      setOtpInvalido(false);
      setField('otpVerified', true);
      if (res.data.otp_token) setField('otp_token', res.data.otp_token);
      if (res.data.draft_token) setField('draft_token', res.data.draft_token);
      setOtpPhaseDone(true);
    } finally {
      setSending(false);
    }
  };

  const skipOtp = () => {
    setAviso('OTP pendiente de backend. Continúa el flujo anterior sin verificación.');
    setField('otpBypass', true);
    setOtpPhaseDone(true);
  };

  const submit = async () => {
    const err = validate({ kind: 'review' });
    if (err) {
      setError(err);
      return;
    }
    setSending(true);
    setError(null);
    try {
      rememberOrg(draft.organizacion);
      const res = await submitSubmission(buildSubmitBody(draft));
      if (!res.ok) throw new Error(res.error.message);

      let score = res.data.score;
      let benchmark = res.data.benchmark ?? null;
      const token = res.data.results_token;
      if (token) {
        const remote = await fetchResults(token);
        if (remote.ok) {
          const rs = remote.data.score;
          // /api/results devuelve ScoreResult crudo + fichas_links al top-level;
          // no pisar el shape del cliente (niveles/promedios anidados).
          score = {
            ...score,
            fichas: rs.fichas ?? score.fichas,
            fichas_niveles: rs.fichas_niveles ?? score.fichas_niveles,
            arquetipo: rs.arquetipo ?? score.arquetipo,
            segmento: rs.segmento ?? score.segmento,
            fichas_links: remote.data.fichas_links ?? score.fichas_links,
            recomendacion_url:
              remote.data.recomendacion_url ?? score.recomendacion_url,
          };
          benchmark = remote.data.benchmark ?? benchmark;
        }
      }
      const resultsIdx = WIZARD_STEPS.findIndex((s) => s.kind === 'resultados');
      const kept: DraftState = {
        ...draft,
        score,
        benchmark,
        results_token: token ?? null,
        draft_token: res.data.draft_token ?? draft.draft_token,
        stepIndex: resultsIdx,
      };
      clearDraft();
      saveDraft(kept);
      setDraft(kept);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSending(false);
    }
  };

  const respondidas = useMemo(
    () =>
      BLOQUES.reduce(
        (acc, b) => acc + b.preguntas.filter((p) => draft.respuestas[p.id]).length,
        0,
      ),
    [draft.respuestas],
  );

  const title = useMemo(() => titleForStep(step, otpPhaseDone), [step, otpPhaseDone]);
  const usaPanel =
    step.kind === 'registro_a' || step.kind === 'registro_b' || step.kind === 'bloque0';
  const showProgress = step.kind === 'bloque0' || step.kind === 'transicion' || step.kind === 'pregunta' || step.kind === 'review';

  if (step.kind === 'landing') {
    return (
      <>
        <div className="shell shell--wide">
          <Landing intro={INTRO_V02} onStart={() => goTo(1)} />
        </div>
        <FooterFramework />
      </>
    );
  }

  const bloqueErrores = error ? (
    <p className="error" role="alert">
      {error}
    </p>
  ) : null;

  const contenido = (
    <>
      {LEGAL_PENDING && (step.kind === 'registro_a' || step.kind === 'registro_b') && (
        <div className="aviso">
          Texto de consentimiento pendiente de revisión legal antes del envío masivo.
        </div>
      )}
      {aviso && usaPanel && (
        <div className="aviso" role="status">
          {aviso}
        </div>
      )}

      {step.kind === 'registro_a' && (
        <>
          <div className="campo">
            <label htmlFor="nombre">Nombre y apellido</label>
            <input
              id="nombre"
              type="text"
              value={draft.nombre}
              onChange={(e) => setField('nombre', e.target.value)}
              autoComplete="name"
              required
            />
          </div>
          <div className="campo">
            <label htmlFor="email">Email corporativo</label>
            <span className="ayuda" id="email-ayuda">
              Allí le enviaremos su perfil de madurez.
            </span>
            <input
              id="email"
              type="email"
              value={draft.email}
              onChange={(e) => setField('email', e.target.value)}
              autoComplete="email"
              aria-describedby="email-ayuda"
              required
            />
          </div>
          <fieldset className="campo campo--grupo">
            <legend className="campo__legend">Código de verificación</legend>
            <span className="ayuda">Elegí cómo recibir el código de 6 dígitos.</span>
            <div className="chips" role="radiogroup" aria-label="Canal OTP">
              <button
                type="button"
                className={`chip${draft.otpChannel === 'email' ? ' chip--activo' : ''}`}
                aria-pressed={draft.otpChannel === 'email'}
                onClick={() => setField('otpChannel', 'email')}
              >
                Email
              </button>
              <button
                type="button"
                className={`chip${draft.otpChannel === 'sms' ? ' chip--activo' : ''}`}
                aria-pressed={draft.otpChannel === 'sms'}
                onClick={() => setField('otpChannel', 'sms')}
              >
                SMS
              </button>
            </div>
          </fieldset>
          {draft.otpChannel === 'sms' && (
            <div className="campo">
              <label htmlFor="phone">Celular (SMS)</label>
              <span className="ayuda" id="phone-ayuda">
                Formato internacional, ej. +54 9 11 1234-5678. Solo para enviar el código.
              </span>
              <input
                id="phone"
                type="tel"
                inputMode="tel"
                value={draft.phone}
                onChange={(e) => setField('phone', e.target.value)}
                autoComplete="tel"
                aria-describedby="phone-ayuda"
                required
              />
            </div>
          )}
          <label className="opcion opcion--consentimiento">
            <input
              type="checkbox"
              checked={draft.consentimiento}
              onChange={(e) => setField('consentimiento', e.target.checked)}
            />
            <span className="opcion__texto">{CONSENT_TEXT}</span>
          </label>
          <input id="company_fax" className="honeypot" tabIndex={-1} autoComplete="off" />
        </>
      )}

      {step.kind === 'registro_b' && (
        <>
          {!otpPhaseDone && (
            <>
              <p className="ayuda" id="otp-ayuda">
                {otpSent
                  ? draft.otpChannel === 'sms'
                    ? `Enviamos un código de 6 dígitos por SMS a ${draft.phone}.`
                    : `Enviamos un código de 6 dígitos a ${draft.email}. Revise también correo no deseado.`
                  : draft.otpChannel === 'sms'
                    ? 'Verifique su celular con el código que le enviamos por SMS.'
                    : 'Verifique su email con el código que le enviamos.'}
              </p>
              <OtpBoxes
                value={otpCode}
                onChange={(v) => {
                  setOtpInvalido(false);
                  setOtpCode(v);
                }}
                disabled={sending}
                invalido={otpInvalido}
                ayudaId="otp-ayuda"
              />
              {bloqueErrores}
              <div className="acciones acciones--otp">
                <Boton variante="fantasma" onClick={back} disabled={sending}>
                  Atrás
                </Boton>
                <Boton
                  variante="secundario"
                  onClick={() => void resendOtp()}
                  disabled={sending || reenvioEn > 0}
                >
                  {reenvioEn > 0 ? `Reenviar en ${reenvioEn}s` : 'Reenviar código'}
                </Boton>
                <Boton variante="primario" onClick={() => void confirmOtp()} disabled={sending}>
                  {sending ? 'Verificando…' : 'Verificar'}
                </Boton>
              </div>
              <div className="acciones acciones--secundarias">
                <Boton variante="fantasma" onClick={skipOtp} disabled={sending}>
                  ¿No llega el código? Continuar sin verificar
                </Boton>
              </div>
            </>
          )}
          {otpPhaseDone && (
            <>
              <div className="campo">
                <label htmlFor="org">Organización</label>
                <span className="ayuda" id="org-ayuda">
                  Escriba para buscar entre las empresas socias de CESSI (orden alfabético).
                </span>
                <SocioCombobox
                  id="org"
                  value={draft.organizacion}
                  onChange={(v) => setField('organizacion', v)}
                  ayudaId="org-ayuda"
                />
              </div>
              <div className="campo">
                <label htmlFor="web">Sitio web de la organización</label>
                <span className="ayuda" id="web-ayuda">
                  Escriba el dominio; el prefijo https:// ya está incluido.
                </span>
                <div className="campo-url">
                  <span className="campo-url__prefijo" aria-hidden="true">
                    https://
                  </span>
                  <input
                    id="web"
                    type="text"
                    inputMode="url"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder="www.empresa.com"
                    value={websiteSinProtocolo(draft.website)}
                    onChange={(e) => setField('website', websiteConHttps(e.target.value))}
                    onBlur={() => {
                      if (draft.website.trim()) {
                        setField('website', websiteConHttps(draft.website));
                      }
                    }}
                    autoComplete="url"
                    aria-describedby="web-ayuda"
                    required
                  />
                </div>
              </div>
              <div className="campo">
                <label htmlFor="li">LinkedIn de la organización</label>
                <span className="ayuda" id="li-ayuda">
                  Opcional.
                </span>
                <input
                  id="li"
                  type="url"
                  value={draft.linkedin}
                  onChange={(e) => setField('linkedin', e.target.value)}
                  aria-describedby="li-ayuda"
                />
              </div>
            </>
          )}
        </>
      )}

      {step.kind === 'bloque0' && (
        <Bloque0Form
          page={step.page}
          draft={draft}
          onPick={(id, value) => {
            setError(null);
            setField(id as keyof DraftState, value as never);
          }}
        />
      )}

      {step.kind === 'transicion' && (
        <TransicionStep
          code={step.code}
          numero={BLOQUES.findIndex((b) => b.code === step.code) + 1}
          total={BLOQUES.length}
          placeholder={BLOQUES.find((b) => b.code === step.code)?.placeholder}
          onBack={back}
          onNext={next}
        />
      )}

      {step.kind === 'pregunta' &&
        (() => {
          const bloque = BLOQUES[step.bloqueIdx]!;
          const preg = bloque.preguntas[step.pregIdx]!;
          return (
            <PreguntaStep
              key={preg.id}
              pregunta={preg}
              code={bloque.code}
              bloqueTitulo={bloque.titulo}
              posicion={step.pregIdx + 1}
              global={flatQuestionIndex(step.bloqueIdx, step.pregIdx) + 1}
              seleccion={draft.respuestas[preg.id] || ''}
              onPick={(letra, autoAdvance = true) => setRespuesta(preg.id, letra, autoAdvance)}
              onBack={back}
              onNext={onContinue}
              error={bloqueErrores}
            />
          );
        })()}

      {step.kind === 'review' && (
        <ReviewAnswers
          draft={draft}
          respondidas={respondidas}
          onEdit={(bloqueIdx, pregIdx) => {
            const idx = WIZARD_STEPS.findIndex(
              (s) => s.kind === 'pregunta' && s.bloqueIdx === bloqueIdx && s.pregIdx === pregIdx,
            );
            if (idx >= 0) goTo(idx);
          }}
        />
      )}

      {step.kind === 'resultados' &&
        (draft.score ? (
          <ResultsView
            score={draft.score}
            benchmark={draft.benchmark}
            organizacion={draft.organizacion}
            respuestas={draft.respuestas}
            nombre={draft.nombre}
            onNuevaEvaluacion={reiniciarEvaluacion}
          />
        ) : (
          <div className="resultados resultados--vacio">
            <h2>Todavía no hay perfil</h2>
            <p className="ayuda">Complete la autoevaluación y envíela para ver su perfil.</p>
            <div className="acciones">
              <Boton variante="primario" onClick={() => goTo(1)}>
                Retomar autoevaluación
              </Boton>
            </div>
          </div>
        ))}

      {step.kind !== 'pregunta' && step.kind !== 'transicion' && !enOtp && bloqueErrores}

      {(step.kind === 'registro_a' || step.kind === 'bloque0' || step.kind === 'review' ||
        (step.kind === 'registro_b' && otpPhaseDone)) && (
        <div className="acciones">
          <Boton variante="fantasma" onClick={back} disabled={draft.stepIndex <= 1 || sending}>
            Atrás
          </Boton>
          {step.kind === 'registro_a' ? (
            <Boton variante="primario" onClick={() => void sendOtp()} disabled={sending}>
              {sending ? 'Enviando…' : 'Continuar'}
            </Boton>
          ) : step.kind === 'review' ? (
            <Boton variante="primario" onClick={() => void submit()} disabled={sending}>
              {sending ? 'Calculando su perfil…' : 'Ver mi perfil'}
            </Boton>
          ) : (
            <Boton variante="primario" onClick={onContinue} disabled={sending}>
              Continuar
            </Boton>
          )}
        </div>
      )}
    </>
  );

  return (
    <>
      <div className="shell">
        <BrandLockup
          className="brand-lockup--shell"
          titulo="Innovación con IA · Autoevaluación"
          subtitulo={FRAMEWORK_SELLO}
          conSolapas
        />

        {showProgress && (
          <div className="progreso-bloque">
            <div className="progreso__meta">
              <span className="progreso__etiqueta">
                {step.kind === 'review' ? 'Revisión' : 'Su progreso se guarda solo'}
              </span>
              <span className="progreso__pct">
                {step.kind === 'pregunta'
                  ? `${flatQuestionIndex(step.bloqueIdx, step.pregIdx) + 1} de ${TOTAL_PREGUNTAS} preguntas`
                  : step.kind === 'review'
                    ? `${respondidas} de ${TOTAL_PREGUNTAS} respondidas`
                    : `${Math.round(progress * 100)}%`}
              </span>
            </div>
            <Progreso valor={progress} />
          </div>
        )}

        <main className="wizard" tabIndex={-1} ref={vistaRef}>
          {usaPanel ? (
            <Panel titulo={title} subtitulo={subtitleForStep(step, otpPhaseDone)}>
              {contenido}
            </Panel>
          ) : (
            contenido
          )}
        </main>
      </div>
      <FooterFramework />
    </>
  );
}

function titleForStep(step: WizardStep, otpPhaseDone = true): string {
  switch (step.kind) {
    case 'registro_a':
      return 'Creemos su perfil';
    case 'registro_b':
      return otpPhaseDone ? 'Su organización' : 'Verifique su identidad';
    case 'bloque0':
      return step.page === 2 ? 'Sobre la organización' : 'Dónde opera y quién responde';
    case 'transicion':
      return DIMENSION_LABELS[step.code];
    case 'pregunta':
      return BLOQUES[step.bloqueIdx]?.titulo ?? 'Pregunta';
    case 'review':
      return 'Revisión de respuestas';
    case 'resultados':
      return 'Su perfil de madurez';
    default:
      return '';
  }
}

function subtitleForStep(step: WizardStep, otpPhaseDone: boolean): string | undefined {
  if (step.kind === 'registro_a') return 'Paso 1 de 2 · Identidad';
  if (step.kind === 'registro_b') {
    return otpPhaseDone ? 'Paso 2 de 2 · Datos de la organización' : 'Paso 2 de 2 · Verificación';
  }
  if (step.kind === 'bloque0') return `Contexto · ${step.page === 2 ? '1' : '2'} de 2`;
  return undefined;
}

function TransicionStep({
  code,
  numero,
  total,
  placeholder,
  onBack,
  onNext,
}: {
  code: keyof typeof TRANSICION_DIMENSION;
  numero: number;
  total: number;
  placeholder?: boolean;
  onBack: () => void;
  onNext: () => void;
}) {
  return (
    <section className="wizard-step wizard-step--transicion" aria-labelledby="transicion-titulo">
      <p className="wizard-dim">
        <span className="wizard-dim__nombre">
          Dimensión {numero} de {total}
        </span>
        <span
          className="badge badge--neutro wizard-dim__conteo"
          style={{ marginLeft: 'auto' }}
        >
          4 preguntas
        </span>
      </p>
      <h2 id="transicion-titulo" className="wizard-step__titulo">
        {DIMENSION_LABELS[code]}
      </h2>
      <p className="transicion">{TRANSICION_DIMENSION[code]}</p>
      {placeholder && (
        <p className="aviso">
          Copy provisional de esta dimensión — reemplazar por Autoevaluación v0.2 textual sin
          cambiar códigos.
        </p>
      )}
      <div className="acciones">
        <Boton variante="fantasma" onClick={onBack}>
          Atrás
        </Boton>
        <Boton variante="primario" onClick={onNext}>
          Comenzar dimensión
        </Boton>
      </div>
    </section>
  );
}

function PreguntaStep({
  pregunta,
  bloqueTitulo,
  posicion,
  global,
  seleccion,
  onPick,
  onBack,
  onNext,
  error,
}: {
  pregunta: PreguntaPuntuable;
  code: string;
  bloqueTitulo: string;
  posicion: number;
  global: number;
  seleccion: OpcionLetra | '';
  /** autoAdvance=false al navegar con flechas (APG radiogroup) para no saltar de pantalla. */
  onPick: (letra: OpcionLetra, autoAdvance?: boolean) => void;
  onBack: () => void;
  onNext: () => void;
  error: ReactNode;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const tituloId = `preg-${pregunta.id}`;

  const focoYSelecciona = (i: number, autoAdvance: boolean) => {
    const total = pregunta.opciones.length;
    const idx = ((i % total) + total) % total;
    const opt = pregunta.opciones[idx];
    if (!opt) return;
    refs.current[idx]?.focus();
    onPick(opt.letra, autoAdvance);
  };

  const onKeyDown = (e: TecladoReact<HTMLDivElement>) => {
    const activo = refs.current.findIndex((el) => el === document.activeElement);
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      focoYSelecciona(activo < 0 ? 0 : activo + 1, false);
      return;
    }
    if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      focoYSelecciona(
        activo < 0 ? pregunta.opciones.length - 1 : activo - 1,
        false,
      );
    }
  };

  // Atajos A–D / 1–4 disponibles sin necesidad de tabular hasta las opciones.
  useEffect(() => {
    const enTeclado = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const destino = e.target as HTMLElement | null;
      if (destino && ['INPUT', 'TEXTAREA', 'SELECT'].includes(destino.tagName)) return;
      const tecla = e.key.toLowerCase();
      const porLetra = LETRAS.indexOf(tecla as OpcionLetra);
      const idx = porLetra >= 0 ? porLetra : ['1', '2', '3', '4'].indexOf(tecla);
      if (idx >= 0 && idx < pregunta.opciones.length) {
        e.preventDefault();
        onPick(pregunta.opciones[idx]!.letra, true);
      }
    };
    document.addEventListener('keydown', enTeclado);
    return () => document.removeEventListener('keydown', enTeclado);
  }, [pregunta, onPick]);

  const seleccionUi = seleccion ? letraParaUi(seleccion) : '';
  const indiceSeleccion = pregunta.opciones.findIndex((o) => o.letra === seleccionUi);

  return (
    <section className="wizard-step" aria-labelledby={tituloId}>
      <p className="wizard-dim">
        <span className="wizard-dim__nombre">{bloqueTitulo}</span>
        <span
          className="badge badge--neutro wizard-dim__conteo"
          style={{ marginLeft: 'auto' }}
        >
          {posicion} de 4
        </span>
      </p>

      <h2 id={tituloId} className="pregunta-texto wizard-step__pregunta">
        {pregunta.texto}
      </h2>
      {pregunta.ayuda && (
        <p className="pregunta-ayuda" id={`${tituloId}-ayuda`}>
          {pregunta.ayuda}
        </p>
      )}

      <div
        className="opciones"
        role="radiogroup"
        aria-labelledby={tituloId}
        aria-describedby={pregunta.ayuda ? `${tituloId}-ayuda` : undefined}
        onKeyDown={onKeyDown}
      >
        {pregunta.opciones.map((o, i) => {
          const activa = seleccionUi === o.letra;
          const enfocable = indiceSeleccion >= 0 ? activa : i === 0;
          const badge = o.letra === 'na' ? 'N/A' : o.letra.toUpperCase();
          return (
            <button
              key={o.letra}
              ref={(el) => {
                refs.current[i] = el;
              }}
              type="button"
              role="radio"
              aria-checked={activa}
              tabIndex={enfocable ? 0 : -1}
              className={`opcion opcion--card${activa ? ' opcion--activa' : ''}`}
              onClick={() => onPick(o.letra, true)}
            >
              <span className="opcion__letra">{badge}</span>
              <span className="opcion__cuerpo">
                <span className="opcion__texto">{o.texto}</span>
                {o.ayuda && <span className="opcion__ayuda">{o.ayuda}</span>}
              </span>
            </button>
          );
        })}
      </div>

      {error}

      <div className="acciones wizard-step__acciones">
        <Boton variante="fantasma" onClick={onBack}>
          Atrás
        </Boton>
        {seleccion && (
          <Boton variante="secundario" onClick={onNext}>
            Continuar
          </Boton>
        )}
      </div>

      <p className="ayuda wizard-step__atajo">
        Pregunta {global} de {TOTAL_PREGUNTAS} · Puede elegir con las teclas A–D o las flechas.
      </p>
    </section>
  );
}

function Bloque0Form({
  page,
  draft,
  onPick,
}: {
  page: 2 | 3;
  draft: DraftState;
  onPick: (id: string, value: string) => void;
}) {
  const list = page === 2 ? BLOQUE0_PAGINA2 : BLOQUE0_PAGINA3;
  return (
    <>
      {list.map((p) => {
        const valor = String(draft[p.id as keyof DraftState] ?? '');
        if (p.id === 'p0_4') {
          const elegidos = parseSectores(
            valor,
            p.opciones.map((o) => o.value),
          );
          const multipleActivo = elegidos.includes(SECTOR_MULTIPLE);
          return (
            <fieldset className="campo campo--grupo campo--multiseleccion" key={p.id}>
              <legend id={`${p.id}-label`}>{p.texto}</legend>
              <p className="ayuda" id={`${p.id}-ayuda`}>
                Seleccionadas: {elegidos.length} de {MAX_SECTORES}.
              </p>
              <div
                className="chips chips--sectores"
                role="group"
                aria-labelledby={`${p.id}-label`}
                aria-describedby={`${p.id}-ayuda`}
              >
                {p.opciones.map((o) => {
                  const activo = elegidos.includes(o.value);
                  const limite = elegidos.length >= MAX_SECTORES && !activo;
                  const deshabilitado =
                    !activo && (limite || (multipleActivo && o.value !== SECTOR_MULTIPLE));
                  return (
                    <button
                      key={o.value}
                      type="button"
                      aria-pressed={activo}
                      disabled={deshabilitado}
                      className={`chip${activo ? ' chip--activo' : ''}`}
                      onClick={() =>
                        onPick(p.id, serializarSectores(toggleSector(elegidos, o.value)))
                      }
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </fieldset>
          );
        }
        const useChips = p.opciones.length <= 6;
        if (useChips) {
          return (
            <div className="campo campo--grupo" key={p.id}>
              <label id={`${p.id}-label`}>{p.texto}</label>
              <div className="chips" role="group" aria-labelledby={`${p.id}-label`}>
                {p.opciones.map((o) => {
                  const activo = valor === o.value;
                  return (
                    <button
                      key={o.value}
                      type="button"
                      aria-pressed={activo}
                      className={`chip${activo ? ' chip--activo' : ''}`}
                      onClick={() => onPick(p.id, o.value)}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        }
        return (
          <div className="campo" key={p.id}>
            <label htmlFor={p.id}>{p.texto}</label>
            <select id={p.id} value={valor} onChange={(e) => onPick(p.id, e.target.value)}>
              <option value="">Seleccione…</option>
              {p.opciones.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        );
      })}
    </>
  );
}

function ReviewAnswers({
  draft,
  respondidas,
  onEdit,
}: {
  draft: DraftState;
  respondidas: number;
  onEdit: (bloqueIdx: number, pregIdx: number) => void;
}) {
  const completo = respondidas === TOTAL_PREGUNTAS;
  return (
    <section className="wizard-step wizard-step--review" aria-labelledby="review-titulo">
      <h2 id="review-titulo" className="wizard-step__titulo">
        Revise antes de enviar
      </h2>
      <p className="ayuda">
        {completo
          ? `Respondió las ${TOTAL_PREGUNTAS} preguntas. Puede editar cualquiera antes de calcular su perfil.`
          : `Le faltan ${TOTAL_PREGUNTAS - respondidas} preguntas por responder.`}
      </p>

      {BLOQUES.map((b, bi) => (
        <section className="review-grupo" key={b.code} aria-labelledby={`review-${b.code}`}>
          <h3 id={`review-${b.code}`} className="review-grupo__titulo">
            {b.titulo}
          </h3>
          <ul className="review-list">
            {b.preguntas.map((preg, pi) => {
              const letra = draft.respuestas[preg.id];
              const letraUi = letra ? letraParaUi(letra) : undefined;
              const opt = preg.opciones.find((o) => o.letra === letraUi);
              return (
                <li
                  key={preg.id}
                  className={`review-item${opt ? '' : ' review-item--falta'}`}
                >
                  <div className="review-item__cuerpo">
                    <p className="review-item__q">{preg.texto}</p>
                    <p className="review-item__a">
                      {opt ? (
                        <>
                          <span className="badge review-item__letra">
                            {letraUi === 'na' ? 'N/A' : letraUi?.toUpperCase()}
                          </span>{' '}
                          {opt.texto}
                        </>
                      ) : (
                        <span className="badge badge--neutro">Sin responder</span>
                      )}
                    </p>
                  </div>
                  <Boton
                    variante="fantasma"
                    onClick={() => onEdit(bi, pi)}
                    aria-label={`${opt ? 'Editar' : 'Responder'} la pregunta: ${preg.texto}`}
                  >
                    {opt ? 'Editar' : 'Responder'}
                  </Boton>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </section>
  );
}

function ResultsView({
  score,
  benchmark,
  organizacion,
  nombre,
  respuestas,
  onNuevaEvaluacion,
}: {
  score: ScorePayload;
  benchmark: BenchmarkPayload | null;
  organizacion: string;
  nombre: string;
  respuestas: Record<string, OpcionLetra | ''>;
  onNuevaEvaluacion: () => void;
}) {
  const fichas = score.fichas ?? [];
  const nivelesFicha = score.fichas_niveles ?? [];
  const links =
    score.fichas_links ??
    fichas.map((codigo, i) => {
      const f = fichaPorCodigo(codigo);
      const nivel = (nivelesFicha[i] ?? 1) as 1 | 2 | 3 | 4;
      return {
        codigo,
        nombre: f?.nombre ?? codigo,
        url: urlCapacidad(FRAMEWORK_BASE, codigo, nivel),
        nivel,
      };
    });
  const recomendacionUrl =
    score.recomendacion_url ??
    (fichas.length === 3
      ? urlRecomendacion({
          baseUrl: FRAMEWORK_BASE,
          fichas,
          nivelesFicha: [
            nivelesFicha[0] ?? 1,
            nivelesFicha[1] ?? 1,
            nivelesFicha[2] ?? 1,
          ],
          nivelesDim: [
            score.niveles.EV,
            score.niveles.PC,
            score.niveles.OD,
            score.niveles.DC,
            score.niveles.GS,
          ],
          respuestas,
          organizacion: organizacion || undefined,
          arquetipo: score.arquetipo,
          segmento: score.segmento,
          fecha: fechaRecomendacion(new Date()),
        })
      : null);
  const material = enlacesResultados();
  const descriptor = DESCRIPTORES_NIVEL[score.nivel_general];
  const detalle = descriptor.replace(/^N\d\s*·\s*/, '');
  const valoresRadar = {
    EV: score.promedios?.EV ?? score.niveles.EV,
    PC: score.promedios?.PC ?? score.niveles.PC,
    OD: score.promedios?.OD ?? score.niveles.OD,
    DC: score.promedios?.DC ?? score.niveles.DC,
    GS: score.promedios?.GS ?? score.niveles.GS,
  };

  return (
    <section className="resultados" aria-labelledby="resultados-titulo">
      <header className="resultados__titular">
        <p className="resultados__eyebrow">Su perfil de madurez</p>
        <h2 id="resultados-titulo" className="resultados__nivel">
          Nivel general N{score.nivel_general}
        </h2>
        <p className="ayuda resultados__minimo">
          Es el nivel de su dimensión más baja: la que marca el ritmo de la adopción.
        </p>
        <p className="resultados__desc">{detalle}</p>
        {(score.arquetipo || score.segmento) && (
          <p className="ayuda">
            {score.arquetipo && (
              <a href={material.perfil} target="_blank" rel="noopener">
                {score.arquetipo}
              </a>
            )}
            {score.arquetipo && score.segmento ? ' · ' : ''}
            {score.segmento}
          </p>
        )}
      </header>

      <ComparacionBenchmark score={score} benchmark={benchmark} />

      <div className="resultados__visual">
        <RadarChart niveles={valoresRadar} foco={score.dimension_foco} />
        <IndiceDial valor={score.indice_IN} />
      </div>

      <section className="resultados__foco" aria-labelledby="foco-titulo">
        <h3 id="foco-titulo">Tres capacidades recomendadas</h3>
        <p className="ayuda">
          Fichas del Framework de Adopción de IA para priorizar en el próximo trimestre.
        </p>
        <ol className="recs">
          {links.map((l) => (
            <li key={l.codigo}>
              <strong>
                {l.codigo} · {l.nombre}
              </strong>
              {l.nivel ? ` (N${l.nivel})` : ''}
              {' — '}
              <a href={l.url} target="_blank" rel="noreferrer">
                Abrir ficha
              </a>
            </li>
          ))}
        </ol>
        {recomendacionUrl && (
          <p className="ayuda">
            <a href={recomendacionUrl} target="_blank" rel="noreferrer">
              Ver mi mapa de las 21 capacidades y el plan de 90 días →
            </a>
          </p>
        )}
        <p className="ayuda resultados__material">
          <a href={material.guia} target="_blank" rel="noopener">
            Guía para directivos: cómo leer su perfil
          </a>
          <a href={material.plantilla90Dias} target="_blank" rel="noopener">
            Plantilla del plan de 90 días (PDF)
          </a>
        </p>
      </section>

      <div className="acciones acciones--resultados">
        <Boton
          variante="secundario"
          onClick={() => {
            try {
              descargarPerfilPdf({
                organizacion,
                nombre,
                score,
                benchmark,
              });
            } catch {
              window.alert(
                'No se pudo generar el PDF. Probá de nuevo o usá el perfil enviado por email.',
              );
            }
          }}
        >
          Descargar perfil (PDF)
        </Boton>
        {MOSTRAR_REINICIO_TEST && (
          <Boton
            variante="fantasma"
            onClick={() => {
              if (
                window.confirm(
                  'Se descarta este perfil en pantalla y empieza una autoevaluación nueva. El envío ya registrado no se borra. ¿Continuar?',
                )
              ) {
                onNuevaEvaluacion();
              }
            }}
          >
            Hacer otra evaluación (test)
          </Boton>
        )}
      </div>

      <p className="ayuda resultados__cierre">
        También le enviaremos este perfil por email a la dirección declarada. Los resultados
        agregados se publican solo de forma anónima.
      </p>
    </section>
  );
}

const DIMENSIONES_RESULTADO = ['EV', 'PC', 'OD', 'DC', 'GS'] as const;

function numeroResultado(valor: number): string {
  return valor.toLocaleString('es-AR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

function ComparacionBenchmark({
  score,
  benchmark,
}: {
  score: ScorePayload;
  benchmark: BenchmarkPayload | null;
}) {
  if (!benchmark?.enabled || !benchmark.promedioPorDimension) {
    const n = benchmark?.n ?? 0;
    const faltan = organizacionesFaltantesBenchmark(n);
    const progreso = Math.min(1, n / BENCHMARK_MIN_N);
    return (
      <section
        className="benchmark-final benchmark-final--pendiente"
        aria-labelledby="benchmark-titulo"
      >
        <p className="resultados__eyebrow">Comparación con la industria</p>
        <h3 id="benchmark-titulo">
          {faltan > 0
            ? `Faltan ${faltan} ${faltan === 1 ? 'organización' : 'organizaciones'} para comparar con el sector`
            : 'La media del sector estará disponible en breve'}
        </h3>
        <p id="benchmark-privacidad">
          Para proteger la privacidad, la comparación contra la media de la industria se publica
          cuando hay al menos {BENCHMARK_MIN_N} organizaciones canónicas. Cuando se habilite, también
          la recibirá por email.
        </p>
        <div className="benchmark-final__muestra">
          <Progreso
            valor={progreso}
            etiqueta={`Muestra sectorial: ${n} de ${BENCHMARK_MIN_N} organizaciones`}
            mostrarPorcentaje
          />
        </div>
      </section>
    );
  }

  const propios = score.promedios ?? score.niveles;
  return (
    <section className="benchmark-final" aria-labelledby="benchmark-titulo">
      <p className="resultados__eyebrow">Comparación con la industria</p>
      <h3 id="benchmark-titulo">Su organización frente a la media</h3>
      <p className="ayuda">
        Comparación anónima sobre {benchmark.n} organizaciones canónicas. Escala 0 a 4.
      </p>
      <div className="benchmark-final__lista">
        {DIMENSIONES_RESULTADO.map((code) => {
          const propio = Number(propios[code] ?? 0);
          const media = benchmark.promedioPorDimension![code];
          const diferencia = propio - media;
          return (
            <div className="benchmark-final__fila" key={code}>
              <div className="benchmark-final__cabecera">
                <strong>{DIMENSION_LABELS[code]}</strong>
                <span className={diferencia >= 0 ? 'benchmark-final__delta--positivo' : ''}>
                  {diferencia >= 0 ? '+' : ''}
                  {numeroResultado(diferencia)} vs. media
                </span>
              </div>
              <div
                className="benchmark-final__barras"
                aria-label={`${DIMENSION_LABELS[code]}: su resultado ${numeroResultado(propio)}, media ${numeroResultado(media)}`}
              >
                <span style={{ width: `${Math.max(0, Math.min(100, (propio / 4) * 100))}%` }} />
                <i style={{ left: `${Math.max(0, Math.min(100, (media / 4) * 100))}%` }} />
              </div>
              <div className="benchmark-final__leyenda">
                <span>Usted: {numeroResultado(propio)}</span>
                <span>Media: {numeroResultado(media)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
